import React from 'react';
import Helmet from 'react-helmet';
import styled from 'styled-components';

import Header from '../components/header';
import TextSection from '../components/TextSection';

import { colors } from '../utils/styles';
import { translate } from '../../i18n';

const t = translate('pages/privacy');

const sections = [
  {
    title: t('Information we collect and receive'),
    paragraphs: [],
    children: [
      {
        title: t('Customer data'),
        paragraphs: [
          {
            text: t(
              'Content and information submitted by users to the Services is referred to in this policy as “Customer Data.” As further explained below, Customer Data is controlled by the organisation or other third party that created the team (the “Customer”). Where Juven collects or processes Customer Data, it does so on behalf of the Customer. Here are some examples of Customer Data (but keep in mind they are only examples and there may be others): posts, pictures, videos, and other types of files. A user may also choose to enter information into their profile, such as first and last name, job, a photo, a phone number and an address.'
            )
          }
        ]
      },
      {
        title: 'Other information',
        paragraphs: [
          {
            text:
              'Juven may also collect and receive the following information:',
            bullets: [
              t(
                'Account creation information. Users provide information such as an email address and password to create an individual or an organisation account.'
              ),
              t(
                'Billing and other information. For Customers that purchase a paid version of the Services, our corporate affiliates and our third party payment processors may collect and store billing address and credit card information on our behalf or we may do this ourselves.'
              ),
              t(
                'Services usage information. This is information about how you are accessing and using the Services, which may include administrative and support communications with us and information about the organisations, members, events, donation campaigns, features, content, and links you interact with, and what third party integrations you use (if any).'
              ),
              t(
                'Contact information. With your permission, any contact information you choose to import is collected (such as an address book from a device) when using the Services.'
              ),
              t(
                'Log data. When you use the Services our servers automatically record information, including information that your browser sends whenever you visit a website or your mobile app sends when you are using it. This log data may include your Internet Protocol address, the address of the web page you visited before using the Services, your browser type and settings, the date and time of your use of the Services, information about your browser configuration and plug-ins, language preferences, and cookie data.'
              ),
              t(
                'Device information. We may collect information about the device you are using the Services on, including what type of device it is, what operating system you are using, device settings, application IDs, unique device identifiers, and crash data. Whether we collect some or all of this information often depends on what type of device you are using and its settings.'
              ),
              t(
                'Services integrations. If, when using the Services, you integrate with a third party service, we will connect that service to ours. The third party provider of the integration may share certain information about your account with Juven. However, we do not receive or store your passwords for any of these third party services.'
              ),
              t(
                'Third party data. Juven may also receive information from affiliates in our corporate group, our partners, or others that we use to make our own information better or more useful. This might be aggregate level information, such as which IP addresses go with which zip codes, or it might be more specific information, such as about how well an online marketing or email campaign performed.'
              )
            ]
          }
        ]
      }
    ]
  },
  {
    title: t('How we use your information'),
    paragraphs: [
      {
        text: t('We use your information to provide and improve our Services.')
      }
    ],
    children: [
      {
        title: t('Customer data'),
        paragraphs: [
          {
            text: t(
              'Juven may access and use Customer Data as reasonably necessary and in accordance with Customer’s instructions to (a) provide, maintain and improve the Services; (b) to prevent or address service, security, technical issues or at a Customer’s request in connection with customer support matters; (c) as required by law or as permitted by the Data Request Policy and (d) as set forth in our agreement with the Customer or as expressly permitted in writing by the Customer. Additional information about Juven’s confidentiality and security practices with respect to Customer Data is available at our Security Practices page.'
            )
          }
        ]
      },
      {
        title: 'Other information',
        paragraphs: [
          {
            text: t(
              'We use other kinds of information in providing the Services. Specifically:'
            ),
            bullets: [
              t(
                'To understand and improve our Services. We carry out research and analyse trends to better understand how users are using the Services and improve them.'
              ),
              t(
                'To communicate with you by: (a) Responding to your requests. If you contact us with a problem or question, we will use your information to respond. (b) Sending emails and Juven messages. We may send you Service and administrative emails and messages. We may also contact you to inform you about changes in our Services, our Service offerings, and important Service related notices, such as security and fraud notices. These emails and messages are considered part of the Services and you may not opt-out of them. In addition, we sometimes send emails about new product features or other news about Juven. You can opt out of these at any time.'
              ),
              t(
                'Billing and account management. We use account data to administer accounts and keep track of billing and payments.'
              ),
              t(
                'Communicating with you and marketing. We often need to contact you for invoicing, account management and similar reasons. We may also use your contact information for our own marketing or advertising purposes. You can opt out of these at any time.'
              ),
              t(
                'Investigating and preventing bad deals from happening. We work hard to keep the Services secure and to prevent abuse and fraud.'
              )
            ]
          },
          {
            text: t(
              'This policy is not intended to place any limits on what we do with data that is aggregated and/or de-identified so it is no longer associated with an identifiable user or Customer of the Services.'
            )
          }
        ]
      }
    ]
  },
  {
    title: t('Sharing and disclosure'),
    paragraphs: [
      {
        text: t(
          'There are times when information described in this privacy policy may be shared by Juven. This section discusses only how Juven may share such information. Customers determine their own policies for the sharing and disclosure of Customer Data. Juven does not control how Customers or their third parties choose to share or disclose Customer Data.'
        )
      }
    ],
    children: [
      {
        title: 'Customer data',
        paragraphs: [
          {
            text: t(
              'Juven may share Customer Data in accordance with our agreement with the Customer and the Customer’s instructions, including:'
            ),
            bullets: [
              t(
                'With third party service providers and agents. We may engage third party companies or individuals to process Customer Data.'
              ),
              t(
                'With affiliates. We may engage affiliates in our corporate group to process Customer Data.'
              ),
              t(
                'With third party integrations. Juven may, acting on our Customer’s behalf, share Customer Data with the provider of an integration added by Customer. Juven is not responsible for how the provider of an integration may collect, use, and share Customer Data.'
              )
            ]
          }
        ]
      },
      {
        title: t('Other information'),
        paragraphs: [
          {
            text: t('Juven may share other information as follows:'),
            bullets: [
              t(
                'About you with the Customer. There may be times when you contact Juven to help resolve an issue specific to a team of which you are a member. In order to help resolve the issue and given our relationship with our Customer, we may share your concern with our Customer.'
              ),
              t(
                'With third party service providers and agents. We may engage third party companies or individuals, such as third party payment processors, to process information on our behalf.'
              ),
              t(
                'With affiliates. We may engage affiliates in our corporate group to process other information.'
              )
            ]
          }
        ]
      },
      {
        title: t('Other types of disclosure'),
        paragraphs: [
          {
            text: t(
              'Juven may share or disclose Customer Data and other information as follows:'
            ),
            bullets: [
              t(
                'During changes to our business structure. If we engage in a merger, acquisition, bankruptcy, dissolution, reorganisation, sale of some or all of Juven\'s assets, financing, acquisition of all or a portion of our business, a similar transaction or proceeding, or steps in contemplation of such activities (e.g. due diligence).'
              ),
              t(
                'To comply with laws. To comply with legal or regulatory requirements and to respond to lawful requests, court orders and legal process.'
              ),
              t(
                'To enforce our rights, prevent fraud and for safety. To protect and defend the rights, property, or safety of us or third parties, including enforcing contracts or policies, or in connection with investigating and preventing fraud.'
              )
            ]
          },
          {
            text: t(
              'We may disclose or use aggregate or de-identified information for any purpose. For example, we may share aggregated or de-identified information with our partners or others for business or research purposes like telling a prospective Juven Customer the average number of events organised within an organisation on Juven in a day.'
            )
          }
        ]
      }
    ]
  },
  {
    title: t('Changes to this Privacy Policy'),
    paragraphs: [
      {
        text: t(
          'We may change this policy from time to time, and if we do we will post any changes on this page. If you continue to use the Services after those changes are in effect, you agree to the revised policy.'
        )
      }
    ]
  },
  {
    title: t('Contacting Juven'),
    className: '_contact',
    paragraphs: [
      {
        text: t(
          'Please also feel free to contact us if you have any questions about Juven’s Privacy Policy or practices. You may contact us at cs@juven.co.'
        )
      }
    ]
  }
];

const Sections = sections.map((s, i) => (
  <TextSection key={ `text-section-${ i }` } section={ s } pos={ i } />
));

const DocumentDiv = styled.div`
  & .content {
    background-color: ${ colors.lightGray };
    padding: 50px 0;

    & .container {
      background-color: white;
      padding: 110px auto;
    }

    & .wrapper {
      width: 100%;
      max-width: 600px;
      padding: 80px 0;
      margin: 0 auto;
    }

    & h3 {
      text-align: center;
      margin-bottom: 0;
      font-size: 36px;
      line-height: 49px;
      font-weight: 700;
      color: ${ colors.darkGray };
    }

    & .subtitle {
      text-align: center;
      font-size: 16px;
      line-height: 29px;
      color: ${ colors.darkGray };
      letter-spacing: 1.5px;
      margin: 0 0 40px 0;
    }

    & .intro {
      padding: 20px;

      & p {
        margin: 0 0 20px 0;
        font-size: 16px;
        line-height: 22px;
      }
    }
  }

  @media (max-width: 900px) {
    & .content .container {
      padding-top: 0;
    }
  }
`;

const CaptionDiv = styled.div`
  padding: 2em 0rem;
`;

export default () => (
  <div>
    <Helmet title={ t('Privacy Policy | Juven') } />
    <Header showLanguage noScroll />

    <div className='content'>
      <DocumentDiv>
        <div className='container' style={ { paddingTop: 80 } }>
          <div className='wrapper'>
            <h3>{ t('Privacy Policy') }</h3>
            <CaptionDiv>
              <p className='subtitle'>{ t('Last updated 9 Dec 2021') }</p>
              <div className='intro'>
                <p>
                  { t(
                    'This Privacy Policy will help you understand what information we collect at Juven, how Juven uses it, and what choices you have regarding this information.'
                  ) }
                </p>
                <p>
                  { t(
                    'When we talk about “Juven,” “we,” “our,” or “us” in this policy, we are referring to Juven Holdings Limited, the company which provides the Services. When we talk about the “Services” in this policy, we are referring to our online tools and platform. Our Services are currently available for use via a web browser or applications specific to your desktop or mobile device.'
                  ) }
                </p>
              </div>
            </CaptionDiv>
            { Sections }
          </div>
        </div>
      </DocumentDiv>
    </div>
  </div>
);



// WEBPACK FOOTER //
// ./src/pages/privacy.js